import styled from '@emotion/styled';
import { Button, Dialog, DialogContent, TextField, Typography } from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useSnackbar } from '../../../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties
} from '../common';
import {
  connectUkg,
  disconnectUkg,
  testUkgConnection,
  useUkgConnectionStatus
} from './service';
import { VerificationResultView } from './VerificationResultView';
import {
  UkgConnectErrorCode,
  UkgConnectionStatus,
  UkgCredentials,
  UkgVerificationResult,
  isUkgCredentialsComplete,
  parseUkgError,
  toUkgCredentialsPayload
} from './verification';

/**
 * The product name, used in dialog copy where the context is already
 * unambiguous.
 */
const NAME = 'UKG Pro';

/**
 * The label on the card in the integrations grid, and the name the grid sorts
 * and searches by. Disambiguated from the Merge-brokered 'UKG Pro' card that
 * MERGE_HRIS_INTEGRATIONS also renders, the same way Merge's ADP card is
 * labelled '(non-API)'. This one talks to UKG directly, which is what makes
 * write-back possible.
 *
 * Exported so the registry entry cannot drift from what the card renders.
 */
export const UKG_CARD_NAME = 'UKG Pro (API)';

const LOGO_URL = '/images/integrations-logos/merge/ukg-pro.png';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const EMPTY_CREDENTIALS: UkgCredentials = {
  serviceUrl: '',
  username: '',
  password: '',
  customerApiKey: '',
  userApiKey: ''
};

/**
 * Falls back to the raw message: the backend always sends one, and it is more
 * specific than anything we could invent for an unexpected code.
 */
const errorMessage = (
  code: string | null,
  message: string,
  t: (s: string) => string
) => {
  if (code === UkgConnectErrorCode.INVALID_REQUEST) {
    return t(
      'UKG Pro rejected the request. Make sure all five fields are filled in, then try again.'
    );
  }
  if (code === UkgConnectErrorCode.NOT_CONFIGURED) {
    return t('There is no stored UKG Pro connection for this organization yet.');
  }
  return message || t('Something went wrong talking to UKG Pro.');
};

const CredentialsForm = ({
  creds,
  onChange
}: {
  creds: UkgCredentials;
  onChange: (update: Partial<UkgCredentials>) => void;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <>
      <FormRow>
        <CustomInputLabel label={t('Service URL')} fullWidth>
          <TextField
            variant="outlined"
            value={creds.serviceUrl}
            onChange={(ev) => onChange({ serviceUrl: ev.target.value })}
            fullWidth
            autoComplete="off"
          />
        </CustomInputLabel>
      </FormRow>

      <FormRow>
        <CustomInputLabel label={t('Service account username')} fullWidth>
          <TextField
            variant="outlined"
            value={creds.username}
            onChange={(ev) => onChange({ username: ev.target.value })}
            fullWidth
            autoComplete="off"
          />
        </CustomInputLabel>
      </FormRow>

      <FormRow>
        <CustomInputLabel label={t('Service account password')} fullWidth>
          <TextField
            variant="outlined"
            type="password"
            value={creds.password}
            onChange={(ev) => onChange({ password: ev.target.value })}
            fullWidth
            autoComplete="new-password"
          />
        </CustomInputLabel>
      </FormRow>

      <FormRow>
        <CustomInputLabel label={t('Customer API Key')} fullWidth>
          <TextField
            variant="outlined"
            type="password"
            value={creds.customerApiKey}
            onChange={(ev) => onChange({ customerApiKey: ev.target.value })}
            fullWidth
            autoComplete="new-password"
          />
        </CustomInputLabel>
      </FormRow>

      <FormRow>
        <CustomInputLabel label={t('User API Key')} fullWidth>
          <TextField
            variant="outlined"
            type="password"
            value={creds.userApiKey}
            onChange={(ev) => onChange({ userApiKey: ev.target.value })}
            fullWidth
            autoComplete="new-password"
          />
        </CustomInputLabel>
      </FormRow>
    </>
  );
};

const ConnectedDetails = ({
  status,
  orgId,
  /** Suppressed right after connecting, when the verification result already says it. */
  showConnectedAlert
}: {
  status: UkgConnectionStatus;
  orgId: string;
  showConnectedAlert: boolean;
}) => {
  const { t } = useUnsafeTranslation();

  const [testResult, setTestResult] = useState<UkgVerificationResult | null>(
    null
  );
  const [testError, setTestError] = useState<string | null>(null);

  return (
    <>
      {showConnectedAlert && (
        <Alert severity="success">{t('Connected')}</Alert>
      )}

      {!status.active && (
        <Alert severity="warning">
          {t('These credentials are stored but the integration is not active.')}
        </Alert>
      )}

      <FormRow>
        <ButtonWithPromise
          variant="outlined"
          color="primary"
          pending={`${t('Testing')}...`}
          onClick={async () => {
            setTestResult(null);
            setTestError(null);
            try {
              setTestResult(await testUkgConnection(orgId));
            } catch (err) {
              const failure = parseUkgError(err);
              // A failed probe still reports per-transport outcomes; only
              // fall back to a message when the payload has none.
              if (failure.verification) {
                setTestResult(failure.verification);
              } else {
                setTestError(errorMessage(failure.code, failure.message, t));
              }
            }
          }}
        >
          {t('Test connection')}
        </ButtonWithPromise>
      </FormRow>

      {testError && <Alert severity="error">{testError}</Alert>}
      {testResult && <VerificationResultView result={testResult} />}
    </>
  );
};

const DisconnectConfirmDialog = ({
  open,
  onClose,
  onConfirm
}: {
  open: boolean;
  onClose: () => void;
  /** Rejects with a user-facing message; the dialog stays open to show it. */
  onConfirm: () => Promise<void>;
}) => {
  const { t } = useUnsafeTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {t('Disconnect UKG Pro?')}
      </DialogTitleWithTypography>
      <Content>
        <Typography component="p">
          {t(
            'This removes the stored UKG credentials for this organization. Imports and write-backs to UKG will stop until it is reconnected.'
          )}
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
      </Content>
      <DialogActionsWithSlots
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="secondary"
              pending={`${t('Disconnecting')}...`}
              onClick={async () => {
                setError(null);
                try {
                  await onConfirm();
                } catch (err) {
                  setError(
                    err instanceof Error && err.message
                      ? err.message
                      : t('Something went wrong talking to UKG Pro.')
                  );
                }
              }}
            >
              {t('Disconnect')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

const SetupDialog = ({
  orgId,
  status,
  loading,
  onRefresh,
  open,
  onClose,
  onSuccessfulIntegration
}: {
  orgId: string;
  status: UkgConnectionStatus | null;
  loading: boolean;
  onRefresh: () => Promise<void> | void;
} & IntegrationSetupDialogProperties) => {
  const { t } = useUnsafeTranslation();
  const { enqueueSnackbarSuccess } = useSnackbar();

  const [creds, setCreds] = useState<UkgCredentials>(EMPTY_CREDENTIALS);
  const [result, setResult] = useState<UkgVerificationResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justConnected, setJustConnected] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  // Never hold entered secrets in memory once the dialog is dismissed.
  useEffect(() => {
    if (!open) {
      setCreds(EMPTY_CREDENTIALS);
      setResult(null);
      setSubmitError(null);
      setJustConnected(false);
      setConfirmingDisconnect(false);
    }
  }, [open]);

  const connected = !!status?.connected;

  const handleClose = () => {
    // Connecting mid-dialog earns the same "now go set up an import source"
    // hand-off every other integration gives; it also closes this dialog.
    if (justConnected) {
      onSuccessfulIntegration();
    }
    onClose();
  };

  const handleDisconnect = async () => {
    try {
      await disconnectUkg(orgId);
    } catch (err) {
      const failure = parseUkgError(err);
      throw new Error(
        failure.message || t('Something went wrong talking to UKG Pro.')
      );
    }
    setConfirmingDisconnect(false);
    setCreds(EMPTY_CREDENTIALS);
    setResult(null);
    setSubmitError(null);
    // Disconnecting undoes a connect made in this same dialog, so closing it
    // must not trigger the "now set up an import source" hand-off.
    setJustConnected(false);
    enqueueSnackbarSuccess(t('UKG Pro disconnected'));
    // Comes back `connected: false`, which swaps the details for the empty
    // credential form.
    await onRefresh();
  };

  const handleConnect = async () => {
    setResult(null);
    setSubmitError(null);
    try {
      const response = await connectUkg(orgId, toUkgCredentialsPayload(creds));
      // Drop the secrets as soon as they are stored — they are not needed
      // again and must not be echoed back into the form.
      setCreds(EMPTY_CREDENTIALS);
      setJustConnected(true);
      setResult(response.verification);
      await onRefresh();
    } catch (err) {
      const failure = parseUkgError(err);
      if (failure.verification) {
        // Which transport failed is the whole answer here — render it instead
        // of a generic message.
        setResult(failure.verification);
      } else {
        setSubmitError(errorMessage(failure.code, failure.message, t));
      }
    }
  };

  if (loading && !status) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitleWithTypography>
          {NAME} {t('org.integrations.integration')}
        </DialogTitleWithTypography>
        <Content>
          <Skeleton />
          <Skeleton />
        </Content>
        <DialogActionsWithSlots
          right={
            <Button color="primary" onClick={handleClose}>
              {t('Close')}
            </Button>
          }
        />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t(
            'Use UKG as a source to import employee data, and save data back into UKG employee profiles'
          )}
        </Alert>

        {!connected ? (
          <CredentialsForm
            creds={creds}
            onChange={(update) => setCreds((c) => ({ ...c, ...update }))}
          />
        ) : (
          <ConnectedDetails
            status={status as UkgConnectionStatus}
            orgId={orgId}
            showConnectedAlert={!justConnected}
          />
        )}

        {submitError && <Alert severity="error">{submitError}</Alert>}
        {result && <VerificationResultView result={result} />}
      </Content>
      <DialogActionsWithSlots
        left={
          connected ? (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setConfirmingDisconnect(true)}
            >
              {t('Disconnect')}
            </Button>
          ) : undefined
        }
        right={
          <>
            <Button color="primary" onClick={handleClose}>
              {t('Close')}
            </Button>
            {!connected && (
              <ButtonWithPromise
                variant="contained"
                color="primary"
                onClick={handleConnect}
                disabled={!isUkgCredentialsComplete(creds)}
                pending={`${t('Connecting')}...`}
              >
                {t('Connect')}
              </ButtonWithPromise>
            )}
          </>
        }
      />
      <DisconnectConfirmDialog
        open={confirmingDisconnect}
        onClose={() => setConfirmingDisconnect(false)}
        onConfirm={handleDisconnect}
      />
    </Dialog>
  );
};

export const UkgProSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { org } = useCurrentUser();
  // `ukg_getConnectionStatus` returns no secrets, so it is safe on page load —
  // and it is the only source available to the FE: the UKG config collection
  // is not client-readable.
  const { status, loading, refresh } = useUkgConnectionStatus(org.id);

  return (
    <IntegrationConfigurationButton
      name={UKG_CARD_NAME}
      functionalities={functionalities}
      logoUrl={LOGO_URL}
      active={!!status?.connected}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          orgId={org.id}
          status={status}
          loading={loading}
          onRefresh={refresh}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
